import { useMemo } from "react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts"
// 你的 UI 元件（若有）
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/card"
import { Typography } from "./ot"
// 若有封裝的 Chart 容器/Tooltip，就沿用；沒有可移除
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart"

export default function SegmentedProgressBar({
    // --- 共用 ---
    mode = "progress", // 'progress' | 'bar'
    title = "",
    description = "",
    statistic_title = "文案",
    data = [
        { label: "手動", value: 8, color: "text-blue-600", bgColor: "bg-blue-500" },
        { label: "系統", value: 12, color: "text-purple-600", bgColor: "bg-purple-500" },
        { label: "智慧", value: 4, color: "text-blue-600", bgColor: "bg-blue-600" },
    ],
    /** 對應鍵：讓元件同時支援 {label,value} 或 {category,count} */
    keys = { label: "label", value: "value" },
    /** 直方圖用到的額外設定（可選） */
    bar = {
        height: 320,
        margin: { top: 20, right: 30, left: 20, bottom: 60 },
        barRadius: [4, 4, 0, 0],
        barColorVar: "var(--color-count)", // 你在 ChartContainer 提供的 CSS 變數
    },
}) {
    const safe = useMemo(() => {
        const labelKey = keys.label || "label"
        const valueKey = keys.value || "value"
        const rows = (Array.isArray(data) ? data : []).map((d) => ({
            _label: d[labelKey],
            _value: Number(d[valueKey]) || 0,
            bgColor: d.bgColor || "bg-gray-400",
        }))
        const total = rows.reduce((s, r) => s + r._value, 0)
        return { rows, total }
    }, [data, keys])

    if (mode === "bar") {
        // 直方圖
        const chartData = safe.rows.map((r) => ({ category: r._label, count: r._value }))
        const total = safe.total

        return (
            <div className="w-full max-w-4xl mx-auto p-6 space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-2xl font-bold">{title}</CardTitle>
                        <CardDescription>各類型{statistic_title}的數量分布統計（總計：{total}）</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {/* 若沒有自訂容器，可直接用 BarChart 包一層 div */}
                        <ChartContainer config={{ count: { label: "數量", color: bar.barColorVar } }}>
                            <BarChart data={chartData} height={bar.height} margin={bar.margin}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="category"
                                    tickLine={false}
                                    axisLine={false}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    interval={0}
                                />
                                <YAxis tickLine={false} axisLine={false} />
                                <ChartTooltip content={<ChartTooltipContent />} cursor={{ fill: "rgba(0,0,0,0.08)" }} />
                                <Bar dataKey="count" radius={bar.barRadius} fill={bar.barColorVar} />
                            </BarChart>
                        </ChartContainer>
                    </CardContent>
                </Card>
            </div>
        )
    }

    // 分段進度條（原來的樣式）
    const total = safe.total || 1 // 避免除以 0
    return (
        <div className="w-full mx-auto p-2 bg-white rounded-lg">
            {/* Header */}
            <div className="flex justify-between items-center mb-1">
                <Typography.P className="font-medium text-gray-800">{title}</Typography.P>
                <span className="text-sm font-semibold text-gray-600">{safe.total}</span>
            </div>

            {/* Progress Bar */}
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-1">
                <div className="h-full flex">
                    {safe.rows.map((segment, index) => {
                        const percentage = (segment._value / total) * 100
                        return (
                            <div
                                key={index}
                                className={`h-full ${segment.bgColor}`}
                                style={{ width: `${percentage}%` }}
                                title={`${segment._label}: ${segment._value}`}
                            />
                        )
                    })}
                </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-sm">
                {safe.rows.map((segment, index) => (
                    <div key={index} className="flex items-center gap-1">
                        <div className={`w-2 h-2 rounded-full ${segment.bgColor}`} />
                        <span className="text-gray-600">
                            {segment._label}: {segment._value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}
